import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { navigation, site } from "@/content";
import { Logo } from "@/components/logo";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 border-b border-line bg-ivory/85 backdrop-blur-xl transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_24px_-16px_rgba(20,23,27,0.25)]" : ""
      }`}
    >
      <div
        className={`mx-auto flex max-w-6xl items-center justify-between px-6 transition-[padding] duration-300 ${
          scrolled ? "py-2.5" : "py-3.5"
        }`}
      >
        <Link to="/" className="flex items-center gap-3" aria-label={site.brandName}>
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          <NavDropdown
            label="Services"
            rootHref="/services"
            items={navigation.services}
            open={servicesOpen}
            setOpen={setServicesOpen}
          />
          <NavDropdown
            label="Industries"
            rootHref="/industries"
            items={navigation.industries}
            open={industriesOpen}
            setOpen={setIndustriesOpen}
          />
          {navigation.main.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="link-underline whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-ink hover:text-teal"
              activeProps={{ className: "text-teal" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to={site.primaryCta.href}
            aria-label={site.primaryCta.label}
            className="btn-primary whitespace-nowrap !px-5 !py-2.5"
          >
            Book a Review
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="rounded-md p-2 text-ink lg:hidden"
        >
          <span className="relative block h-5 w-5">
            <Menu
              className={`absolute inset-0 h-5 w-5 transition-all duration-200 ${open ? "rotate-90 opacity-0" : "rotate-0 opacity-100"}`}
            />
            <X
              className={`absolute inset-0 h-5 w-5 transition-all duration-200 ${open ? "rotate-0 opacity-100" : "-rotate-90 opacity-0"}`}
            />
          </span>
        </button>
      </div>

      {open && (
        <div className="reveal-pop border-t border-line bg-ivory lg:hidden">
          <div className="mx-auto max-w-6xl space-y-6 px-6 py-6">
            <MobileGroup
              label="Services"
              rootHref="/services"
              items={navigation.services}
              onNavigate={() => setOpen(false)}
            />
            <MobileGroup
              label="Industries"
              rootHref="/industries"
              items={navigation.industries}
              onNavigate={() => setOpen(false)}
            />
            <div className="grid gap-2">
              {navigation.main.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-base font-medium text-ink"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <Link
              to={site.primaryCta.href}
              onClick={() => setOpen(false)}
              className="btn-primary w-full"
            >
              {site.primaryCta.label}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function NavDropdown({
  label,
  rootHref,
  items,
  open,
  setOpen,
}: {
  label: string;
  rootHref: string;
  items: { label: string; href: string }[];
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Link
        to={rootHref}
        className="link-underline inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-ink hover:text-teal"
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </Link>
      {open && (
        <div className="reveal-pop elev-3 absolute left-0 top-full min-w-[300px] border border-line bg-card p-1">
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="block px-3.5 py-2.5 text-sm text-ink transition-colors hover:bg-stone"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileGroup({
  label,
  rootHref,
  items,
  onNavigate,
}: {
  label: string;
  rootHref: string;
  items: { label: string; href: string }[];
  onNavigate: () => void;
}) {
  return (
    <div>
      <Link to={rootHref} onClick={onNavigate} className="block text-sm font-medium text-teal">
        {label}
      </Link>
      <div className="mt-2 grid gap-1">
        {items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            className="block py-1.5 text-[15px] text-ink"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
